import { create } from "zustand";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { toast } from "sonner";
import { triggerEvent } from "@/lib/utils";

export type ResetPasswordState = {
  isSendingCode: boolean;
  isChangingPassword: boolean;
  passwordChanged: boolean;
  codeSent: boolean;
  error: null | Error;
};

export const resetPasswordState = create<ResetPasswordState>(() => ({
  isSendingCode: false,
  isChangingPassword: false,
  passwordChanged: false,
  codeSent: false,
  error: null,
}));

export const resetPasswordStateIsError = (error: Error) =>
  resetPasswordState.setState(() => ({
    error: error,
    isSendingCode: false,
    passwordChanged: false,
    codeSent: false,
  }));

export const resetResetPasswordState = () =>
  resetPasswordState.setState(() => resetPasswordState.getInitialState());

export type SendCodeByEmailToResetPasswordParams = {
  email: string;
};

export const sendCodeByEmailToResetPassword = async ({
  email,
}: SendCodeByEmailToResetPasswordParams) => {
  resetPasswordState.setState(() => ({
    error: null,
    isSendingCode: true,
    passwordChanged: false,
    codeSent: false,
  }));

  try {
    const response = await resetPassword({ username: email });
    if (
      response.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE"
    ) {
      toast.success(
        `Le code de rénitialisation a été envoyé sur cet email: ${email}`,
        {
          duration: 2000,
        },
      );
      resetPasswordState.setState(() => ({
        error: null,
        isSendingCode: false,
        passwordChanged: false,
        codeSent: true,
      }));

      triggerEvent("Password Reset Requested");
    }
  } catch (error) {
    resetPasswordStateIsError(new Error(error as string));
  }
};

export type ConfirmByEmailToResetPasswordParams = {
  email: string;
  password: string;
  code: string;
};

export const confirmByEmailToResetPassword = async ({
  email,
  password,
  code,
}: ConfirmByEmailToResetPasswordParams) => {
  resetPasswordState.setState(() => ({
    error: null,
    isSendingCode: true,
    passwordChanged: false,
    codeSent: false,
  }));

  try {
    await confirmResetPassword({
      username: email,
      newPassword: password,
      confirmationCode: code,
    });
    toast.success(`Votre mot de passe a été mis à jour avec succès.`, {
      duration: 2000,
    });

    resetPasswordState.setState(() => ({
      passwordChanged: true,
    }));
  } catch (error) {
    resetPasswordStateIsError(new Error(error as string));
  }
};
